import { useEffect, useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../../services/api/config';
import { useAuth } from '../../contexts/AuthContext';
import { hubForPath, HubItem } from '../../config/navHubs';

const MANAGER_ROLES = ['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin'];
const ADMIN_ROLES = ['admin', 'company-super-admin', 'super-super-admin'];

/**
 * Sub-navigation for the current hub. Renders the hub's modules as a tab bar
 * that navigates between existing routes. Shows nothing on standalone hubs
 * (Dashboard, Leave) or routes that don't belong to a hub (settings, admin, …).
 */
export default function HubTabs() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [enabledModules, setEnabledModules] = useState<string[] | null>(null); // null = not loaded

  useEffect(() => {
    let active = true;
    apiClient.get('/company/modules')
      .then((res) => {
        if (!active) return;
        const mods = (res.data?.data || []).filter((m: { isActive: boolean }) => m.isActive).map((m: { moduleName: string }) => m.moduleName);
        setEnabledModules(mods);
      })
      .catch(() => { if (active) setEnabledModules([]); });
    return () => { active = false; };
  }, []);

  const isManager = !!user && MANAGER_ROLES.includes(user.role);
  const isAdmin = !!user && ADMIN_ROLES.includes(user.role);

  const allowed = (i: HubItem): boolean => {
    // Optimistic before modules load (enabledModules === null) to avoid a flash.
    if (i.moduleName && enabledModules !== null && !enabledModules.includes(i.moduleName)) return false;
    if (i.managerOnly && !isManager) return false;
    if (i.adminOnly && !isAdmin) return false;
    return true;
  };

  const hub = hubForPath(location.pathname);
  if (!hub) return null;
  const items = hub.items.filter(allowed);
  if (items.length <= 1) return null;

  // Longest-path match wins so /tasks/manager selects "Team Board", not "My Tasks" (/tasks is a prefix).
  let activeIdx = 0, bestLen = -1;
  items.forEach((i, idx) => {
    if ((location.pathname === i.path || location.pathname.startsWith(i.path + '/')) && i.path.length > bestLen) {
      activeIdx = idx; bestLen = i.path.length;
    }
  });

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, mt: -0.5 }}>
      <Tabs
        value={activeIdx}
        onChange={(_, v) => navigate(items[v].path)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {items.map((it) => (
          <Tab key={it.path} label={it.label} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
        ))}
      </Tabs>
    </Box>
  );
}
